import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import IconButton from 'LuxKit/Button/IconButton'
import LinePlayIcon from 'LuxKit/Icons/line/LinePlayIcon'
import LinePauseIcon from 'LuxKit/Icons/line/LinePauseIcon'
import LineVolumeUpIcon from 'LuxKit/Icons/line/LineVolumeUpIcon'
import LineVolumeMuteIcon from 'LuxKit/Icons/line/LineVolumeMuteIcon'
import LineShareIcon from 'LuxKit/Icons/line/LineShareIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { isHeadlessModeSel } from 'selectors/configSelectors'
import { useWrappedStorySelector, useWrappedStoryDispatch, WrappedStoryActions } from 'contexts/Wrapped'
import useWrappedShare from '../../hooks/useWrappedShare'

interface Props {
  onClose: () => void
}

function WrappedHeaderActions({ onClose }: Props) {
  const intl = useIntl()
  const dispatch = useWrappedStoryDispatch()
  const { handleShare } = useWrappedShare()
  const isHeadless = useAppSelector(isHeadlessModeSel)

  // Use granular selectors to minimize re-renders
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const isMuted = useWrappedStorySelector(state => state.isMuted)
  const data = useWrappedStorySelector(state => state.data)

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  const pauseAriaLabel = intl.formatMessage({ id: 'ZyG+4AoAVA', defaultMessage: 'Pause', description: 'Aria-label for pause button' })
  const playAriaLabel = intl.formatMessage({ id: 'AV/hcZ7Wug', defaultMessage: 'Play', description: 'Aria-label for play button' })
  const muteAriaLabel = intl.formatMessage({ id: 'rnRtxyT+TS', defaultMessage: 'Mute', description: 'Aria-label for mute button' })
  const unmuteAriaLabel = intl.formatMessage({ id: 'gNlBUpph/r', defaultMessage: 'Unmute', description: 'Aria-label for unmute button' })

  const handleTogglePause = useCallback(() => {
    dispatch({ type: WrappedStoryActions.togglePause, isPaused: undefined })
  }, [dispatch])

  const handleToggleMute = useCallback(() => {
    dispatch({ type: WrappedStoryActions.toggleMute, isMuted: undefined })
  }, [dispatch])

  return (
    <Group direction="horizontal" gap={4} verticalAlign="center">
      <IconButton
        kind="tertiary"
        variant="ghost"
        size="small"
        onClick={handleTogglePause}
        aria-label={isPaused ? playAriaLabel : pauseAriaLabel}
      >
        {isPaused ? <LinePlayIcon /> : <LinePauseIcon />}
      </IconButton>
      <IconButton
        kind="tertiary"
        variant="ghost"
        size="small"
        onClick={handleToggleMute}
        aria-label={isMuted ? unmuteAriaLabel : muteAriaLabel}
      >
        {isMuted ? <LineVolumeMuteIcon /> : <LineVolumeUpIcon />}
      </IconButton>
      {!isTotalsOnlyMode && <IconButton
        kind="tertiary"
        variant="ghost"
        size="small"
        onClick={handleShare}
        aria-label={intl.formatMessage({ id: 'YooC41iTSv', defaultMessage: 'Share', description: 'Aria-label for share button' })}
      >
        <LineShareIcon />
      </IconButton>}
      {!isHeadless && (
        <IconButton
          kind="tertiary"
          variant="ghost"
          size="small"
          onClick={onClose}
          aria-label={intl.formatMessage({ id: 'XMFDhHZFVd', defaultMessage: 'Close', description: 'Aria-label for close button' })}
        >
          <LineTimesIcon />
        </IconButton>
      )}
    </Group>
  )
}

export default React.memo(WrappedHeaderActions)
